import { z } from 'zod';

import { CAMPAIGN_NAME_MAX_LENGTH, CampaignContentTranslationsSchema } from '../campaign/campaign';
import { CampaignSegmentSchema } from '../campaign-segment/campaign-segment';
import { PreferenceChannelSchema } from '../notification-channel/notification-channel';

/**
 * The editable half of a campaign.
 *
 * A raw shape so the update schema can `.partial()` it — the discipline
 * `NotificationTemplateWritableShape` and `ViolationTypeWritableShape` keep.
 *
 * **`templateKey` is not here**, and its absence is load-bearing rather than an
 * oversight: preference bypass is a per-key catalogue fact, so an operator who
 * could name the key could compose an announcement that reaches members who
 * switched announcements off. A staff-composed campaign is always
 * `announcements.campaign`, and the seeded-copy kind is created server-side by
 * the listener that has a reason to.
 *
 * **`status` is not here either.** A campaign moves through its lifecycle by the
 * verbs that own each transition — send, cancel, the scheduler's claim — never
 * by a client patching a column. That is what makes N27's claim the only door.
 */
export const CampaignWritableShape = {
  name: z.string().trim().min(1).max(CAMPAIGN_NAME_MAX_LENGTH),
  segment: CampaignSegmentSchema,
  /**
   * When to send it, or null to leave it a draft the operator sends by hand.
   *
   * Validated against "not in the past" by the service rather than the schema:
   * the clock is the deployment's (N3), and a schema that read the client's
   * would refuse a legitimate time to an operator in another zone.
   */
  scheduledAt: z.iso.datetime().nullable(),
  translations: CampaignContentTranslationsSchema,
  /**
   * Which channels to send on. Optional until the composer offers the choice
   * (P21e3); absent means every channel the key declares, exactly as a campaign
   * composed before the email channel existed. At least one when given — a
   * campaign to nobody is a mistake, not a draft.
   */
  channels: z.array(PreferenceChannelSchema).min(1).optional(),
};

export const CampaignCreateSchema = z.object(CampaignWritableShape).strict();

export type CampaignCreateRequest = z.infer<typeof CampaignCreateSchema>;
